// --- START OF FILE ModelViewer.react.js ---

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Import utilities for Dash 2/3 compatibility
// Adjust the path based on your file structure (assuming dash3.js is compiled)
import { useLoadingState, setPersistence, getChildLayout, setComponentProps } from '../utils/dash3'; // Example path

// --- Dash 3 Compatibility Notes ---
// 1. Default Props: Moved from `ModelViewer.defaultProps` to function signature (below).
// 2. Loading State: Uses `useLoadingState` utility (if needed) to handle Dash 2/3.
// 3. Persistence: Uses `setPersistence` utility (if needed) called *outside* the component definition.
// 4. Child Layout: Uses `getChildLayout` utility (if needed) for inspecting children.
// 5. Typing: Generated by `dash-generate-components` v3+. Requires adding 'proptypes.js'
//    to _js_dist in your package's __init__.py if using TypeScript source.
// ---

// Component definition with default props in the signature
const DashModelViewer = ({
  // Required props (no defaults needed here)
  id,
  src,
  alt,

  // Props with defaults defined directly here
  cameraControls = true,
  touchAction = "pan-y",
  ar = true,
  hotspots = [],
  style = {},
  toneMapping = 'neutral',
  arModes = "basic_annotations scene-viewer quick-look",
  shadowIntensity = 0,
  arScale = 'auto',
  interpolationDecay = 50,
  fieldOfView = 'auto',
  minFieldOfView = '25deg',
  maxFieldOfView = 'auto',
  maxCameraOrbit = 'auto auto auto',
  minCameraOrbit = 'auto auto auto',
  arButtonText = 'View in your space',
  customArPrompt = null,
  customArFailure = null,
  variantName = null, // Explicit null default

  // Props without defaults (will be undefined if not provided by user)
  cameraOrbit,
  cameraTarget,
  poster,

  // Dash internal props (passed automatically)
  setProps,
  loading_state // Passed by Dash for both v2 and v3
}) => {
  const modelViewerRef = useRef(null);

  // --- Example: Using the loading state utility ---
  // If you needed to render differently based on loading state:
  // const isLoading = useLoadingState(loading_state);
  // if (isLoading) {
  //   return <div>Loading model...</div>;
  // }
  // ---

  // Effect to load the model-viewer script and set up initial listeners
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js"]');
    let script;

    const initializeModelViewer = () => {
      customElements.whenDefined('model-viewer').then(() => {
         if (modelViewerRef.current) {
            setupModelViewerListeners(); // Run combined setup
         }
      });
    };

    if (!existingScript) {
        script = document.createElement('script');
        script.src = "https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js";
        script.type = "module";
        script.addEventListener('load', initializeModelViewer);
        document.body.appendChild(script);
    } else {
        if (customElements.get('model-viewer')) {
            initializeModelViewer();
        } else {
            customElements.whenDefined('model-viewer').then(() => {
                if (modelViewerRef.current) {
                   initializeModelViewer();
                }
            });
        }
    }

    return () => {
      // Cleanup script if it was added by this instance
      if (script && script.parentElement === document.body) {
        document.body.removeChild(script);
      }
      // Note: Robust listener cleanup might require storing handler references
      // outside the setup functions or using useCallback if handlers depend on props/state.
    };
  }, []); // Empty dependency array: run only once on mount

  // Combined setup function for listeners
  const setupModelViewerListeners = () => {
        setupHotspots(); // Setup/update hotspot listeners
        setupAR();       // Setup/update AR listeners
   };


  // Setup Hotspots - including CLICK logic for camera views
  const setupHotspots = () => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const handleAnnotationClick = (event) => {
      const annotation = event.currentTarget;
      const modelViewerElement = modelViewerRef.current;
      if (!modelViewerElement) return;
      const dataset = annotation.dataset;

      let updatedProps = {}; // Collect props to update via setProps

      if (dataset.target && dataset.orbit) {
          modelViewerElement.cameraTarget = dataset.target;
          modelViewerElement.cameraOrbit = dataset.orbit;
          // Update Dash state if these props are controllable
          // updatedProps.cameraTarget = dataset.target; // Example
          // updatedProps.cameraOrbit = dataset.orbit;   // Example

          if (dataset.fov) {
             modelViewerElement.fieldOfView = dataset.fov;
             // updatedProps.fieldOfView = dataset.fov; // Example
          } else {
             modelViewerElement.fieldOfView = '45deg'; // Default FoV for camera view hotspots
             // updatedProps.fieldOfView = '45deg'; // Example
          }
      }
      // Add other click logic if needed

      // Call setProps if there are changes to report back to Dash
      if (Object.keys(updatedProps).length > 0 && setProps) {
        // setProps(updatedProps); // Uncomment if you add controllable props based on clicks
      }
    };

    const hotspotElements = modelViewer.querySelectorAll('.hotspot');

    // Re-attach listeners
    hotspotElements.forEach((hotspot) => {
      hotspot.removeEventListener('click', handleAnnotationClick); // Remove first
      hotspot.addEventListener('click', handleAnnotationClick);   // Add updated handler
    });
  };

  // Setup AR Listeners and Button Logic
  const setupAR = () => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const arButton = modelViewer.querySelector('#ar-button');
    const arPrompt = modelViewer.querySelector('#ar-prompt');
    const arFailure = modelViewer.querySelector('#ar-failure');

    // Use stable function references or useCallback if needed for robust removal
    const handleARButtonClick = () => { if (modelViewer?.canActivateAR) { modelViewer.activateAR(); } };
    const handleArStatus = (event) => { if (arPrompt) { arPrompt.style.display = event.detail.status === 'session-started' ? 'block' : 'none'; } };
    const handleArTracking = (event) => { if (arFailure) { arFailure.style.display = event.detail.status === 'not-tracking' ? 'block' : 'none'; } };

    if (arButton) {
        arButton.removeEventListener('click', handleARButtonClick); // Attempt removal
        arButton.addEventListener('click', handleARButtonClick);
    }
    modelViewer.removeEventListener('ar-status', handleArStatus); // Attempt removal
    modelViewer.addEventListener('ar-status', handleArStatus);
    modelViewer.removeEventListener('ar-tracking', handleArTracking); // Attempt removal
    modelViewer.addEventListener('ar-tracking', handleArTracking);
  };

  // Effect to re-run setupHotspots when the 'hotspots' prop changes
  // Note: React's default comparison is shallow. If hotspot objects *within* the array
  // change identity or content without the array itself changing, this might not re-run.
  // For complex cases, you might need a deep comparison or a different trigger.
  useEffect(() => {
    customElements.whenDefined('model-viewer').then(() => {
       if (modelViewerRef.current) {
            setupHotspots(); // Re-attach click listeners
       }
    });
  }, [hotspots]); // Dependency on the hotspots prop array

  // Convert boolean props for attributes where needed
  // model-viewer attributes often just need to exist, not have a value='true'
  const cameraControlsAttr = cameraControls ? '' : null;
  const arAttr = ar ? '' : null;

  return (
    <model-viewer
      ref={modelViewerRef}
      id={id}
      src={src}
      alt={alt}
      // Pass attributes directly using the derived values
      camera-controls={cameraControls ? '' : undefined} // Use undefined to remove attribute if false
      touch-action={touchAction}
      camera-orbit={cameraOrbit}
      camera-target={cameraTarget}
      field-of-view={fieldOfView}
      min-field-of-view={minFieldOfView}
      max-field-of-view={maxFieldOfView}
      interpolation-decay={interpolationDecay}
      min-camera-orbit={minCameraOrbit}
      max-camera-orbit={maxCameraOrbit}
      poster={poster}
      ar={ar ? '' : undefined} // Use undefined to remove attribute if false
      style={style}
      tone-mapping={toneMapping}
      ar-modes={arModes}
      shadow-intensity={shadowIntensity}
      ar-scale={arScale}
      // Handle 'default' variant name - model-viewer expects attribute removal for default
      variant-name={variantName === 'default' ? undefined : variantName}
    >
      {/* Render hotspot divs based on the hotspots prop array */}
      {hotspots && hotspots.map((hotspot, index) => (
        <div
          key={hotspot.slot || `hotspot-${index}`}
          className={`hotspot ${hotspot.children_classname || ''}`.trim()}
          slot={hotspot.slot || `hotspot-${index + 1}`}
          data-position={hotspot.position}
          data-normal={hotspot.normal}
          // Conditionally add data attributes only if they exist in the hotspot object
          {...(hotspot.orbit && { 'data-orbit': hotspot.orbit })}
          {...(hotspot.target && { 'data-target': hotspot.target })}
          {...(hotspot.fov && { 'data-fov': hotspot.fov })}
        >
          {/* Text content (button label) */}
          {hotspot.text}
        </div>
      ))}

      {/* AR Button Slot */}
      <div id="ar-button" slot="ar-button">
        {arButtonText} {/* Default value handled in props destructuring */}
      </div>

      {/* AR Prompt Slot */}
      <div id="ar-prompt" style={{display: 'none'}}>
         {/* Default value (null) handled in props destructuring, render conditionally */}
        {customArPrompt || <img src="https://modelviewer.dev/assets/hand.png" alt="AR Prompt"/>}
      </div>

      {/* AR Failure Slot */}
      <div id="ar-failure" style={{display: 'none'}}>
         {/* Default value (null) handled in props destructuring, render conditionally */}
        {customArFailure || "AR is not tracking!"}
      </div>
    </model-viewer>
  );
};

// --- Remove the defaultProps block ---
// ModelViewer.defaultProps = { ... }; // DELETED

// --- Keep Prop Types ---
// Prop types are still needed for dash-generate-components and developer documentation
DashModelViewer.propTypes = {
  /** Component ID */
  id: PropTypes.string.isRequired,
  /** Model source URL (.glb or .gltf) */
  src: PropTypes.string.isRequired,
  /** Alt text for accessibility */
  alt: PropTypes.string.isRequired,
  /** Enable camera controls */
  cameraControls: PropTypes.bool,
  /** Touch action behavior */
  touchAction: PropTypes.oneOf(['pan-y', 'pan-x', 'none']),
  /** Initial camera orbital position ($theta $phi $radius) */
  cameraOrbit: PropTypes.string,
  /** Initial camera target point ($X $Y $Z) */
  cameraTarget: PropTypes.string,
  /** Camera field of view */
  fieldOfView: PropTypes.string,
  /** Minimum camera field of view */
  minFieldOfView: PropTypes.string,
  /** Maximum camera field of view */
  maxFieldOfView: PropTypes.string,
  /** Camera interpolation decay rate */
  interpolationDecay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Minimum camera orbit bounds */
  minCameraOrbit: PropTypes.string,
  /** Maximum camera orbit bounds */
  maxCameraOrbit: PropTypes.string,
  /** Poster image URL */
  poster: PropTypes.string,
  /** Enable AR features */
  ar: PropTypes.bool,

  /** Array of hotspot objects passed from the server */
  hotspots: PropTypes.arrayOf(PropTypes.shape({
    slot: PropTypes.string,
    position: PropTypes.string,
    normal: PropTypes.string,
    orbit: PropTypes.string,    // Camera orbit for click
    target: PropTypes.string,   // Camera target for click
    fov: PropTypes.string,      // Optional: Field of view for click
    text: PropTypes.string,
    children_classname: PropTypes.string, // CSS class name (e.g., 'view-button')
  })),

  /** Text for the default AR button */
  arButtonText: PropTypes.string,
  /** Custom React element for the AR prompt */
  customArPrompt: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Custom React element for the AR failure message */
  customArFailure: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** CSS Style object */
  style: PropTypes.object,

  // --- Rendering & AR Prop Types ---
  toneMapping: PropTypes.oneOf(['neutral', 'aces', 'agx', 'reinhard', 'cineon', 'linear', 'none']),
  arModes: PropTypes.string,
  shadowIntensity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  arScale: PropTypes.oneOf(['auto', 'fixed']),
  variantName: PropTypes.string,

  /** Dash-assigned callback that should be called whenever any of the
   * properties change. */
  setProps: PropTypes.func,

  /** Object that holds the loading state object coming from dash-renderer */
  loading_state: PropTypes.shape({
    /** Determines if the component is loading or not */
    is_loading: PropTypes.bool,
    /** Holds which property is loading */
    prop_name: PropTypes.string,
    /** Holds the name of the component that is loading */
    component_name: PropTypes.string,
  }),
};

// --- Example: Setting Persistence (if needed) ---
// If you wanted 'cameraOrbit' and 'cameraTarget' to persist in local storage:
// setPersistence(ModelViewer, ['cameraOrbit', 'cameraTarget']);
// --- This call MUST be outside the component function definition ---

export default DashModelViewer;
// --- END OF FILE ModelViewer.react.js ---